import React, { useEffect, useRef, useState } from 'react';
import { string } from 'prop-types';
import { Helmet } from 'react-helmet-async';
import { useDispatch } from 'react-redux';

import { LayoutSingleColumn, ListingCard, Page } from '../../components';
import { useConfiguration } from '../../context/configurationContext';
import { denormalisedResponseEntities } from '../../util/data';
import { createImageVariantConfig } from '../../util/sdkLoader';
import { parse } from '../../util/urlHelpers';
import { catalogSearchListings, useFirebaseCatalog } from '../../util/api';
import TopbarContainer from '../TopbarContainer/TopbarContainer';
import FooterContainer from '../FooterContainer/FooterContainer';
import { blogArticles, getBlogArticleConfig } from './BlogArticlePage.config';

import css from './BlogArticlePage.module.css';

const EMPTY_SHOP_SECTIONS = [];

const queryArticleShopListings = (queryConfig, listingImageConfig) => (
  _dispatch,
  _getState,
  sdk
) => {
  const apiSearchParams = parse(queryConfig.searchQuery, {
    latlngBounds: ['bounds'],
  });

  delete apiSearchParams.mapSearch;

  if (useFirebaseCatalog()) {
    const { keywords, pub_categoryLevel1, authorId } = apiSearchParams;
    return catalogSearchListings({
      keywords,
      pub_categoryLevel1,
      authorId,
      perPage: Math.max(queryConfig.perPage || 8, 8),
    });
  }

  const { aspectWidth = 4, aspectHeight = 3, variantPrefix = 'listing-card' } =
    listingImageConfig || {};
  const aspectRatio = aspectHeight / aspectWidth;

  return sdk.listings.query({
    ...apiSearchParams,
    pub_listingType: 'sell-new-products',
    page: 1,
    perPage: Math.max(queryConfig.perPage || 8, 8),
    minStock: 1,
    stockMode: 'match-undefined',
    include: ['author', 'author.profileImage', 'images', 'currentStock'],
    'fields.listing': [
      'title',
      'price',
      'deleted',
      'state',
      'publicData.listingType',
      'publicData.transactionProcessAlias',
      'publicData.unitType',
      'publicData.cardStyle',
      'publicData.aggregateRating',
      'publicData.numberOfRatings',
      'publicData.videos',
      'publicData.ready_for_sale',
      'publicData.preparationHours',
    ],
    'fields.user': ['profile.displayName', 'profile.abbreviatedName'],
    'fields.image': [`variants.${variantPrefix}`, `variants.${variantPrefix}-2x`],
    ...createImageVariantConfig(variantPrefix, 400, aspectRatio),
    ...createImageVariantConfig(`${variantPrefix}-2x`, 800, aspectRatio),
    'limit.images': 1,
  });
};

const renderParagraphContent = paragraph => {
  if (Array.isArray(paragraph)) {
    return paragraph.map((part, index) => {
      if (typeof part === 'string') {
        return <React.Fragment key={`${part}-${index}`}>{part}</React.Fragment>;
      }

      if (part?.href) {
        return (
          <a className={css.inlineLink} href={part.href} key={`${part.href}-${index}`}>
            {part.label}
          </a>
        );
      }

      return <React.Fragment key={index}>{part?.label || part?.text || ''}</React.Fragment>;
    });
  }

  return paragraph;
};

const renderSectionImageAboveTitle = (section, article, css) => {
  if (!section.image || article.sectionImageLayout === 'side') {
    return null;
  }

  return (
    <img
      className={css.sectionImage}
      src={section.image.src}
      alt={section.image.alt || section.title}
      width={section.image.width}
      height={section.image.height}
    />
  );
};

const renderSectionInner = (section, article, css) => {
  const sectionBody = (
    <>
      {(section.paragraphs || []).map(paragraph => (
        <p className={css.paragraph} key={JSON.stringify(paragraph)}>
          {renderParagraphContent(paragraph)}
        </p>
      ))}
      {section.bullets ? (
        <ul className={css.list}>
          {section.bullets.map(bullet => (
            <li key={bullet}>{bullet}</li>
          ))}
        </ul>
      ) : null}
      {section.table ? (
        <div className={css.tableWrap}>
          <table className={css.table}>
            {section.table.headers ? (
              <thead>
                <tr>
                  {section.table.headers.map(h => (
                    <th key={h}>{h}</th>
                  ))}
                </tr>
              </thead>
            ) : null}
            <tbody>
              {section.table.rows.map((row, i) => (
                <tr key={i}>
                  {row.map((cell, j) => (
                    <td key={j}>{cell}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}
      {section.paragraphsAfterBullets
        ? section.paragraphsAfterBullets.map(paragraph => (
            <p className={css.paragraph} key={JSON.stringify(paragraph)}>
              {renderParagraphContent(paragraph)}
            </p>
          ))
        : null}
    </>
  );

  if (article.sectionImageLayout === 'side' && section.image) {
    return (
      <div className={css.sectionSplit}>
        <div className={css.sectionContent}>{sectionBody}</div>
        <div className={css.sectionImageWrap}>
          <img
            className={css.sectionImageCompact}
            src={section.image.src}
            alt={section.image.alt || section.title}
            width={section.image.width}
            height={section.image.height}
          />
        </div>
      </div>
    );
  }

  return <>{sectionBody}</>;
};

const BlogArticlePage = props => {
  const { pageId } = props;
  const article = getBlogArticleConfig(pageId);
  const config = useConfiguration();
  const dispatch = useDispatch();
  const rootUrl = config?.marketplaceRootURL || 'https://localflower.ca';
  const guidesCarouselRef = useRef(null);
  const guidesCarouselDragRef = useRef({
    isDragging: false,
    moved: false,
    startX: 0,
    scrollLeft: 0,
  });
  const [shopListings, setShopListings] = useState([]);
  const [shopListingsInProgress, setShopListingsInProgress] = useState(false);
  const [shopListingsError, setShopListingsError] = useState(null);
  const [shopListingsByKey, setShopListingsByKey] = useState({});
  const [shopListingsInProgressByKey, setShopListingsInProgressByKey] = useState({});
  const [shopListingsErrorByKey, setShopListingsErrorByKey] = useState({});

  const shopSections = article?.shopSections || EMPTY_SHOP_SECTIONS;

  useEffect(() => {
    if (!article?.shopSection) {
      setShopListings([]);
      setShopListingsInProgress(false);
      setShopListingsError(null);
      return undefined;
    }

    let cancelled = false;
    setShopListingsInProgress(true);
    setShopListingsError(null);

    dispatch(queryArticleShopListings(article.shopSection, config?.layout?.listingImage))
      .then(response => {
        if (cancelled) {
          return;
        }

        const listings = denormalisedResponseEntities(response).filter(
          listing => !listing.attributes?.deleted && listing.attributes?.state === 'published'
        );
        setShopListings(listings);
      })
      .catch(error => {
        if (!cancelled) {
          setShopListingsError(error);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setShopListingsInProgress(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [article, config?.layout?.listingImage, dispatch]);

  useEffect(() => {
    if (!shopSections.length) {
      setShopListingsByKey({});
      setShopListingsInProgressByKey({});
      setShopListingsErrorByKey({});
      return undefined;
    }

    let cancelled = false;
    const initialProgress = shopSections.reduce(
      (acc, section) => ({ ...acc, [section.key]: true }),
      {}
    );

    setShopListingsByKey({});
    setShopListingsInProgressByKey(initialProgress);
    setShopListingsErrorByKey({});

    shopSections.forEach(section => {
      dispatch(queryArticleShopListings(section, config?.layout?.listingImage))
        .then(response => {
          if (cancelled) {
            return;
          }

          const listings = denormalisedResponseEntities(response).filter(
            listing => !listing.attributes?.deleted && listing.attributes?.state === 'published'
          );

          setShopListingsByKey(prev => ({ ...prev, [section.key]: listings }));
        })
        .catch(error => {
          if (!cancelled) {
            setShopListingsErrorByKey(prev => ({ ...prev, [section.key]: error }));
          }
        })
        .finally(() => {
          if (!cancelled) {
            setShopListingsInProgressByKey(prev => ({ ...prev, [section.key]: false }));
          }
        });
    });

    return () => {
      cancelled = true;
    };
  }, [shopSections, config?.layout?.listingImage, dispatch]);

  if (!article) {
    return null;
  }

  const fallbackGuideLinks = Object.values(blogArticles)
    .filter(item => item.path !== article.path)
    .slice(0, 4)
    .map(item => ({
      label: item.breadcrumbLabel || item.heroTitle,
      href: item.path,
    }));

  const guideLinks = article.sidebarGuides?.length > 0 ? article.sidebarGuides : fallbackGuideLinks;

  // Contextual internal links woven into the article body as natural prose
  // (not cards): one early sentence and one near the end, each with an inline
  // link to the matching wishes page and to flowers to send. Rule-based on the
  // slug, so it needs no per-article data and never names a city.
  const CONTEXT_LINK_RULES = [
    { m: /funeral|sympathy|condolence/, w: { label: 'condolence messages', href: '/wishes/condolence-messages' }, p: { label: 'sympathy flowers', href: '/flower-delivery' } },
    { m: /wedding/, w: { label: 'wedding wishes', href: '/wishes/wedding-wishes' }, p: { label: 'wedding flowers', href: '/flower-delivery' } },
    { m: /anniversary/, w: { label: 'anniversary wishes', href: '/wishes/anniversary-wishes' }, p: { label: 'anniversary flowers', href: '/flower-delivery' } },
    { m: /graduation|prom/, w: { label: 'graduation wishes', href: '/wishes/graduation-wishes' }, p: { label: 'graduation flowers', href: '/flower-delivery' } },
    { m: /get-well/, w: { label: 'get well soon messages', href: '/wishes/get-well-soon-messages' }, p: { label: 'get-well flowers', href: '/flower-delivery' } },
    { m: /new-baby|baby-shower/, w: { label: 'new baby wishes', href: '/wishes/new-baby-wishes' }, p: { label: 'new baby flowers', href: '/flower-delivery' } },
    { m: /mothers-day/, w: { label: "Mother's Day messages", href: '/wishes/mothers-day-messages' }, p: { label: "Mother's Day flowers", href: '/flower-delivery' } },
    { m: /fathers-day/, w: { label: "Father's Day messages", href: '/wishes/fathers-day-messages' }, p: { label: "Father's Day flowers", href: '/flower-delivery' } },
    { m: /valentine/, w: { label: "Valentine's Day messages", href: '/wishes/valentines-day-messages' }, p: { label: "Valentine's flowers", href: '/flower-delivery' } },
    { m: /christmas/, w: { label: 'Christmas wishes', href: '/wishes/christmas-wishes' }, p: { label: 'Christmas flowers', href: '/flower-delivery' } },
    { m: /thanksgiving/, w: { label: 'Thanksgiving messages', href: '/wishes/thanksgiving-messages' }, p: { label: 'Thanksgiving flowers', href: '/flower-delivery' } },
    { m: /housewarming/, w: { label: 'housewarming wishes', href: '/wishes/housewarming-wishes' }, p: { label: 'housewarming flowers', href: '/flower-delivery' } },
    { m: /retirement/, w: { label: 'retirement wishes', href: '/wishes/retirement-wishes' }, p: { label: 'flowers to send', href: '/flower-delivery' } },
    { m: /birthday/, w: { label: 'birthday wishes', href: '/wishes/birthday-wishes' }, p: { label: 'birthday flowers', href: '/flower-delivery' } },
  ];
  const ctx = CONTEXT_LINK_RULES.find(r => r.m.test(article.path || '')) || {
    w: { label: 'card messages and wishes', href: '/wishes' },
    p: { label: 'fresh flowers', href: '/flower-delivery' },
  };
  const earlyContextParagraph = ['Looking for the right words? Our ', ctx.w, ' give you lines you can copy straight onto the card.'];
  const lateContextParagraph = ['And when you are ready to send something, browse ', ctx.p, ' and add your note at checkout.'];

  const sourceSections = article.sections || [];
  const augmentedSections = sourceSections.length
    ? sourceSections.map((section, i) => {
        const adds = [];
        if (i === 0) adds.push(earlyContextParagraph);
        if (i === sourceSections.length - 1) adds.push(lateContextParagraph);
        return adds.length
          ? { ...section, paragraphsAfterBullets: [...(section.paragraphsAfterBullets || []), ...adds] }
          : section;
      })
    : sourceSections;

  const guideCards = guideLinks.map(link => {
    const linkedArticle = Object.values(blogArticles).find(item => item.path === link.href);
    return {
      ...link,
      image: linkedArticle?.heroImage || null,
      description: link.description || linkedArticle?.description || '',
    };
  });
  const monthCards = (article.monthCards || []).map(card => {
    const linkedArticle = card.href
      ? Object.values(blogArticles).find(item => item.path === card.href)
      : null;

    return {
      ...card,
      image: card.image || linkedArticle?.heroImage || null,
    };
  });

  const socialSharing = {
    title: article.title,
    description: article.description,
    images1200: article.socialImage
      ? [
          {
            name: 'facebook',
            url: `${rootUrl}${article.socialImage.src}`,
            width: article.socialImage.width || 1200,
            height: article.socialImage.height || 900,
          },
        ]
      : undefined,
    images600: article.socialImage
      ? [
          {
            name: 'twitter',
            url: `${rootUrl}${article.socialImage.src}`,
            width: 600,
            height: 450,
          },
        ]
      : undefined,
  };

  const schema = [
    {
      '@context': 'https://schema.org',
      '@type': 'Article',
      headline: article.heroTitle,
      description: article.description,
      mainEntityOfPage: `${rootUrl}${article.path}`,
      image: article.socialImage ? [`${rootUrl}${article.socialImage.src}`] : undefined,
      author: {
        '@type': 'Organization',
        name: 'LocalFlower',
      },
      publisher: {
        '@type': 'Organization',
        name: 'LocalFlower',
        logo: {
          '@type': 'ImageObject',
          url: config?.branding?.logoImageMobileURL,
        },
      },
    },
    {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        {
          '@type': 'ListItem',
          position: 1,
          name: 'Home',
          item: rootUrl,
        },
        {
          '@type': 'ListItem',
          position: 2,
          name: 'Blog',
          item: `${rootUrl}/blog`,
        },
        {
          '@type': 'ListItem',
          position: 3,
          name: article.breadcrumbLabel,
          item: `${rootUrl}${article.path}`,
        },
      ],
    },
    {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: (article.faq || []).map(item => ({
        '@type': 'Question',
        name: item.question,
        acceptedAnswer: {
          '@type': 'Answer',
          text: item.answer,
        },
      })),
    },
  ].filter(entry => entry['@type'] !== 'FAQPage' || (article.faq && article.faq.length));

  const renderSectionsInSingleCard = article.renderSectionsInSingleCard !== false;
  const hasHeroImage = !!article.heroImage;
  const isMeaningArticle = article.path?.startsWith('/blog/meaning-of-');
  const guideRowsFirst = article.guideRowsFirst === true && guideCards.length > 0;
  const heroImageAfterIntro = hasHeroImage && article.heroImagePlacement === 'afterIntro';
  const bottomGuidesIsCarousel = guideCards.length > 0;
  const bottomGuidesClassName = `${css.guidesGrid} ${css.guidesCarousel}`;
  const startGuidesCarouselDrag = event => {
    if (!bottomGuidesIsCarousel || event.button !== 0 || !guidesCarouselRef.current) {
      return;
    }

    guidesCarouselDragRef.current = {
      isDragging: true,
      moved: false,
      startX: event.clientX,
      scrollLeft: guidesCarouselRef.current.scrollLeft,
    };
    guidesCarouselRef.current.classList.add(css.guidesCarouselDragging);
  };
  const moveGuidesCarouselDrag = event => {
    const drag = guidesCarouselDragRef.current;
    const carousel = guidesCarouselRef.current;

    if (!bottomGuidesIsCarousel || !drag.isDragging || !carousel) {
      return;
    }

    const distance = event.clientX - drag.startX;

    if (Math.abs(distance) > 4) {
      drag.moved = true;
      event.preventDefault();
    }

    carousel.scrollLeft = drag.scrollLeft - distance;
  };
  const stopGuidesCarouselDrag = () => {
    if (!bottomGuidesIsCarousel || !guidesCarouselRef.current) {
      return;
    }

    const movedWhileDragging = guidesCarouselDragRef.current.moved;
    guidesCarouselDragRef.current.isDragging = false;
    guidesCarouselRef.current.classList.remove(css.guidesCarouselDragging);

    if (movedWhileDragging) {
      setTimeout(() => {
        guidesCarouselDragRef.current.moved = false;
      }, 150);
    }
  };
  const preventGuideClickAfterDrag = event => {
    if (!guidesCarouselDragRef.current.moved) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();
    guidesCarouselDragRef.current.moved = false;
  };
  const renderHeroImage = wrapperClassName => (
    <div className={wrapperClassName}>
      <img
        className={css.heroImage}
        src={article.heroImage.src}
        alt={article.heroImage.alt || article.heroTitle}
        width={article.heroImage.width}
        height={article.heroImage.height}
      />
    </div>
  );

  return (
    <Page
      title={article.title}
      description={article.description}
      socialSharing={socialSharing}
      schema={schema}
      className={css.root}
    >
      <Helmet>
        <link rel="canonical" href={`${rootUrl}${article.path}`} />
      </Helmet>
      <LayoutSingleColumn
        className={css.layoutWrapper}
        topbar={<TopbarContainer />}
        footer={<FooterContainer />}
      >
        <main className={css.content}>
          <section className={css.hero}>
            <div className={css.breadcrumb}>
              <a className={css.breadcrumbLink} href="/">
                Home
              </a>
              <span>/</span>
              <a className={css.breadcrumbLink} href="/blog">
                Blog
              </a>
              <span>/</span>
              <span>{article.breadcrumbLabel}</span>
            </div>

            {article.heroLayout === 'wide' ? (
              <div
                className={`${css.heroCardWide} ${
                  !hasHeroImage || heroImageAfterIntro ? css.heroCardWideSingle : ''
                }`}
              >
                <div className={css.heroContentWide}>
                  <div className={css.metaRow}>
                    <span className={css.metaChip}>{article.updatedLabel}</span>
                    <span className={css.metaChip}>{article.readTime}</span>
                  </div>
                  <h1 className={css.title}>{article.heroTitle}</h1>
                  <p className={css.subtitle}>{article.heroSubtitle}</p>
                  <ul className={css.introList}>
                    {article.introPoints.map(point => (
                      <li key={point}>{point}</li>
                    ))}
                  </ul>
                  {heroImageAfterIntro
                    ? renderHeroImage(`${css.heroImagePanel} ${css.heroImageInlinePanel}`)
                    : null}
                </div>
                {hasHeroImage && !heroImageAfterIntro ? renderHeroImage(css.heroImagePanel) : null}
              </div>
            ) : (
              <div
                className={`${css.heroGrid} ${
                  !hasHeroImage || heroImageAfterIntro ? css.heroGridSingle : ''
                }`}
              >
                <div className={css.heroCopy}>
                  <div className={css.metaRow}>
                    <span className={css.metaChip}>{article.updatedLabel}</span>
                    <span className={css.metaChip}>{article.readTime}</span>
                  </div>
                  <h1 className={css.title}>{article.heroTitle}</h1>
                  <p className={css.subtitle}>{article.heroSubtitle}</p>
                  <ul className={css.introList}>
                    {article.introPoints.map(point => (
                      <li key={point}>{point}</li>
                    ))}
                  </ul>
                  {heroImageAfterIntro
                    ? renderHeroImage(`${css.heroImageCard} ${css.heroImageInlinePanel}`)
                    : null}
                </div>
                {hasHeroImage && !heroImageAfterIntro ? renderHeroImage(css.heroImageCard) : null}
              </div>
            )}
          </section>

          {monthCards.length > 0 ? (
            <section className={css.section}>
              <div className={css.monthCardsSection}>
                <div className={css.monthCardsHeader}>
                  <p className={css.guidesEyebrow}>
                    {article.monthCardsEyebrow || 'Browse by month'}
                  </p>
                  <h2 className={css.guidesTitle}>
                    {article.monthCardsTitle || 'Explore every month'}
                  </h2>
                  {article.monthCardsDescription ? (
                    <p className={css.guidesDescription}>{article.monthCardsDescription}</p>
                  ) : null}
                </div>
                <div className={css.monthCardsGrid}>
                  {monthCards.map(card => (
                    <article className={css.monthCard} key={card.title}>
                      {card.image ? (
                        card.href ? (
                          <a className={css.monthCardImageLink} href={card.href}>
                            <img
                              className={css.monthCardImage}
                              src={card.image.src}
                              alt={card.image.alt || card.title}
                              width={card.image.width}
                              height={card.image.height}
                              loading="lazy"
                            />
                          </a>
                        ) : (
                          <div className={css.monthCardImageLink}>
                            <img
                              className={css.monthCardImage}
                              src={card.image.src}
                              alt={card.image.alt || card.title}
                              width={card.image.width}
                              height={card.image.height}
                              loading="lazy"
                            />
                          </div>
                        )
                      ) : null}
                      <div className={css.monthCardBody}>
                        {card.eyebrow ? (
                          <p className={css.monthCardEyebrow}>{card.eyebrow}</p>
                        ) : null}
                        <h3 className={css.monthCardTitle}>
                          {card.href ? (
                            <a className={css.monthCardTitleLink} href={card.href}>
                              {card.title}
                            </a>
                          ) : (
                            card.title
                          )}
                        </h3>
                        <p className={css.monthCardDescription}>{card.description}</p>
                        {card.links?.length ? (
                          <div className={css.monthCardLinks}>
                            {card.links.map(link => (
                              <a className={css.monthCardLink} href={link.href} key={link.href}>
                                {link.label}
                              </a>
                            ))}
                          </div>
                        ) : card.href ? (
                          <a className={css.monthCardCta} href={card.href}>
                            Open month guide
                          </a>
                        ) : null}
                      </div>
                    </article>
                  ))}
                </div>
              </div>
            </section>
          ) : null}

          {guideRowsFirst ? (
            <section className={css.guidesSection}>
              <div className={css.guidesCard}>
                <div className={css.guidesHeader}>
                  <p className={css.guidesEyebrow}>Keep reading</p>
                  <h2 className={css.guidesTitle}>
                    {article.sidebarGuidesTitle || 'More flower guides'}
                  </h2>
                  <p className={css.guidesDescription}>
                    {article.guideRowsDescription ||
                      'Explore a few more flower stories, meanings, and seasonal ideas after this post.'}
                  </p>
                </div>
                <div className={css.guideRows}>
                  {guideCards.map(link => (
                    <a className={css.guideRow} href={link.href} key={link.href}>
                      <div className={css.guideRowBody}>
                        <p className={css.guideRowEyebrow}>Flower meaning</p>
                        <h3 className={css.guideRowTitle}>{link.label}</h3>
                        {link.description ? (
                          <p className={css.guideRowDescription}>{link.description}</p>
                        ) : null}
                        <span className={css.guideRowLink}>Open guide</span>
                      </div>
                      {link.image ? (
                        <div className={css.guideRowImageWrap}>
                          <img
                            className={css.guideRowImage}
                            src={link.image.src}
                            alt={link.image.alt || link.label}
                            width={link.image.width}
                            height={link.image.height}
                            loading="lazy"
                          />
                        </div>
                      ) : null}
                    </a>
                  ))}
                </div>
              </div>
            </section>
          ) : null}

          <section className={css.section}>
            <div className={`${css.articleGrid} ${css.articleGridWide}`}>
              <div className={css.articleBody}>
                {renderSectionsInSingleCard ? (
                  <article className={css.sectionCard}>
                    {augmentedSections.map((section, index) => (
                      <section className={css.sectionBlock} key={section.title}>
                        {renderSectionImageAboveTitle(section, article, css)}
                        <h2 className={css.sectionTitle}>{section.title}</h2>
                        {renderSectionInner(section, article, css)}
                        {index < augmentedSections.length - 1 ? (
                          <div className={css.sectionDivider} />
                        ) : null}
                      </section>
                    ))}
                  </article>
                ) : (
                  augmentedSections.map(section => (
                    <article className={css.sectionCard} key={section.title}>
                      {renderSectionImageAboveTitle(section, article, css)}
                      <h2 className={css.sectionTitle}>{section.title}</h2>
                      {renderSectionInner(section, article, css)}
                    </article>
                  ))
                )}
              </div>
            </div>
          </section>

          {isMeaningArticle && !article.hideMeaningHubCallout ? (
            <section className={css.section}>
              <div className={`${css.articleGrid} ${css.articleGridWide}`}>
                <div className={css.articleBody}>
                  <article className={css.sectionCard}>
                    <p className={css.paragraph}>
                      If you want to browse all of our flower symbolism guides in one place, start
                      with the{' '}
                      <a className={css.inlineLink} href="/blog/flower-meanings">
                        flower meanings hub
                      </a>
                      .
                    </p>
                  </article>
                </div>
              </div>
            </section>
          ) : null}

          {article.faq && article.faq.length ? (
          <section className={css.faqSection}>
            <h2 className={css.faqTitle}>{article.faqTitle}</h2>
            <div className={css.faqGrid}>
              {article.faq.map(item => (
                <article className={css.faqCard} key={item.question}>
                  <h3 className={css.faqQuestion}>{item.question}</h3>
                  <p className={css.faqAnswer}>{item.answer}</p>
                </article>
              ))}
            </div>
          </section>
          ) : null}

          {guideCards.length > 0 && !article.hideBottomGuides ? (
            <section className={css.guidesSection}>
              <div className={css.guidesCard}>
                <div className={css.guidesHeader}>
                  <p className={css.guidesEyebrow}>Keep reading</p>
                  <h2 className={css.guidesTitle}>
                    {article.sidebarGuidesTitle || 'More flower guides'}
                  </h2>
                  <p className={css.guidesDescription}>
                    Explore a few more flower stories, meanings, and seasonal ideas after this post.
                  </p>
                </div>
                <div
                  className={bottomGuidesClassName}
                  ref={bottomGuidesIsCarousel ? guidesCarouselRef : null}
                  onMouseDown={startGuidesCarouselDrag}
                  onMouseMove={moveGuidesCarouselDrag}
                  onMouseUp={stopGuidesCarouselDrag}
                  onMouseLeave={stopGuidesCarouselDrag}
                  onClickCapture={preventGuideClickAfterDrag}
                >
                  {guideCards.map(link => (
                    <a className={css.guideCard} href={link.href} key={link.href} draggable={false}>
                      {link.image ? (
                        <div className={css.guideImageWrap}>
                          <img
                            className={css.guideImage}
                            src={link.image.src}
                            alt={link.image.alt || link.label}
                            width={link.image.width}
                            height={link.image.height}
                            loading="lazy"
                            draggable={false}
                          />
                        </div>
                      ) : null}
                      <div className={css.guideBody}>
                        <h3 className={css.guideTitle}>{link.label}</h3>
                        <span className={css.guideLink}>Open guide</span>
                      </div>
                    </a>
                  ))}
                </div>
                <div className={css.guidesFooter}>
                  <a className={css.blogCta} href="/blog">
                    Browse all blog posts
                  </a>
                </div>
              </div>
            </section>
          ) : null}

          {shopSections.length > 0 ? (
            <section className={css.shopSection}>
              <div className={css.shopSectionsStack}>
                {shopSections.map(section => {
                  const sectionListings = shopListingsByKey[section.key] || [];
                  const sectionInProgress = shopListingsInProgressByKey[section.key];
                  const sectionError = shopListingsErrorByKey[section.key];

                  return (
                    <div className={css.shopSectionCard} key={section.key}>
                      <div className={css.shopSectionHeader}>
                        <div>
                          <p className={css.shopEyebrow}>{section.eyebrow}</p>
                          <h2 className={css.shopTitle}>{section.title}</h2>
                          <p className={css.shopDescription}>{section.description}</p>
                        </div>
                      </div>

                      {sectionInProgress ? (
                        <p className={css.shopState}>Loading matching listings...</p>
                      ) : null}

                      {!sectionInProgress && sectionListings.length > 0 ? (
                        <div className={css.shopCarousel}>
                          {sectionListings.map(listing => (
                            <div className={css.shopListingCardWrap} key={listing.id.uuid}>
                              <ListingCard
                                listing={listing}
                                className={css.shopListingCard}
                                renderSizes="(min-width: 1280px) 248px, (min-width: 768px) 240px, 72vw"
                                showAuthorInfo={false}
                              />
                              {section.listingHref ? (
                                <a
                                  className={css.shopListingCardOverlay}
                                  href={section.listingHref}
                                  aria-label={`Browse more ${section.title}`}
                                />
                              ) : null}
                            </div>
                          ))}
                        </div>
                      ) : null}

                      {!sectionInProgress && sectionListings.length === 0 ? (
                        <p className={css.shopState}>
                          {sectionError
                            ? 'We could not load matching listings just now, but the full page is still available.'
                            : 'Matching listings will appear here as soon as active products are available.'}
                        </p>
                      ) : null}

                      <div className={css.shopFooter}>
                        <a className={css.shopCta} href={section.href}>
                          {section.ctaLabel}
                        </a>
                      </div>
                    </div>
                  );
                })}
              </div>
            </section>
          ) : null}

          {article.shopSection ? (
            <section className={css.shopSection}>
              <div className={css.shopSectionCard}>
                <div className={css.shopSectionHeader}>
                  <div>
                    <p className={css.shopEyebrow}>{article.shopSection.eyebrow}</p>
                    <h2 className={css.shopTitle}>{article.shopSection.title}</h2>
                    <p className={css.shopDescription}>{article.shopSection.description}</p>
                  </div>
                </div>

                {shopListingsInProgress ? (
                  <p className={css.shopState}>Loading live bouquets...</p>
                ) : null}

                {!shopListingsInProgress && shopListings.length > 0 ? (
                  <div className={css.shopGridFrame}>
                    <div className={css.shopGrid}>
                      {shopListings.map(listing => (
                        <div className={css.shopListingCardWrap} key={listing.id.uuid}>
                          <ListingCard
                            listing={listing}
                            className={css.shopListingCard}
                            renderSizes="(min-width: 1280px) 248px, (min-width: 1024px) calc((100vw - 220px) / 4), (min-width: 768px) calc((100vw - 120px) / 2), calc((100vw - 92px) / 2)"
                            showAuthorInfo={false}
                          />
                          {article.shopSection.listingHref ? (
                            <a
                              className={css.shopListingCardOverlay}
                              href={article.shopSection.listingHref}
                              aria-label="Browse more matching flower listings"
                            />
                          ) : null}
                        </div>
                      ))}
                    </div>
                    <div className={css.shopGridFade} aria-hidden="true" />
                  </div>
                ) : null}

                {!shopListingsInProgress && shopListings.length === 0 ? (
                  <p className={css.shopState}>
                    {shopListingsError
                      ? 'We could not load live bouquets just now, but you can still browse the full fresh flowers page.'
                      : 'Live bouquets will appear here as soon as active matching listings are available.'}
                  </p>
                ) : null}

                <div className={css.shopFooter}>
                  <h3 className={css.shopFooterTitle}>Need more flower options?</h3>
                  <p className={css.shopFooterText}>
                    Open the full listings page to browse more fresh flowers, bouquets, and gifts on
                    LocalFlower.
                  </p>
                  <a className={css.shopCta} href={article.shopSection.href}>
                    {article.shopSection.ctaLabel}
                  </a>
                </div>
              </div>
            </section>
          ) : null}
        </main>
      </LayoutSingleColumn>
    </Page>
  );
};

BlogArticlePage.defaultProps = {
  pageId: null,
};

BlogArticlePage.propTypes = {
  pageId: string,
};

export default BlogArticlePage;
